import { z } from 'zod'
import { IField, IPrice } from '@/client/types'
import {
  ArtworkServiceOptionModel,
  Button,
  CalculatorStep,
  ErrorMessages,
  Extra,
  Help,
  IgeneralPackagesQuery,
  RandomText,
  Resume,
  ServiceModel,
  Step7,
  Step8
} from '@/client/types/Calculator'
import { ICalculatorTemplatePreset, IOrderTemplate } from '@/client/types/OrderTemplate'

export type FieldType =
  | 'select-radio'
  | 'input-number'
  | 'select-radio-vertical'
  | 'textarea'
  | 'checkbox'
  | 'range'
  | 'select-card-checkbox'

export type CalculatorProps = {
  serviceType?: string
  isArtworkService?: boolean
  showAsWizard?: boolean
  template?: IOrderTemplate
  compactView?: boolean
  onContinueShopping?: () => void
}
export const calculatorTemplateProps = [
  'label_type',
  'quantity',
  'sorts',
  'width',
  'height',
  'diameter',
  'label_shape',
  'adhesive',
  'paper',
  'colour',
  'varnish',
  'opaque',
  'double_hit',
  'finishing',
  'foil',
  'die',
  'machine_processing',
  'winding_direction',
  'core_diameter',
  'assembly',
  'labels_on_roll',
  'max_roll_diameter',
  'artwork_service',
  'express_production',
  'super_express_production',
  'job_type'
] as const

export type CalculatorTemplateProp = typeof calculatorTemplateProps[number]

/**
 * Applies the properties of a given calculator template to a selection object.
 *
 * @template TSelection - A generic type extending `Record<string, unknown>`.
 * @param {CalculatorTemplate | undefined} template - The calculator template containing preset values to apply. If undefined, the selection is returned unchanged.
 * @param {TSelection} selection - The original selection object that may be modified based on the template.
 * @returns {Record<string, unknown>} A new object representing the updated selection with applied template properties.
 *
 * The function iterates over a predefined set of template property keys (`calculatorTemplateProps`). For each key:
 * - If the template has a defined preset for the key, the associated value is added to the selection.
 * - If the key is `'double_hit'`, the value is wrapped in an array.
 * - Otherwise, the raw value from the preset is applied to the selection.
 *
 * If no template is provided (`template` is undefined), the function directly returns the original selection object.
 */
export const applyCalculatorTemplateToSelection = <TSelection extends Record<string, unknown>>(
  template: CalculatorTemplate | undefined,
  selection: TSelection
): Record<string, unknown> => {
  if (!template) {
    return selection
  }

  const updatedSelection: Record<string, unknown> = { ...selection }

  calculatorTemplateProps.forEach((key) => {
    const preset = template[key]

    if (!preset) {
      return
    }

    updatedSelection[key] = key === 'double_hit' ? [preset.value] : preset.value
  })

  return updatedSelection
}

export type CalculatorTemplate = {
  id: number
  name: string // A name that will be used to identify the template for the customer
  description?: string
  customer_id?: number
  frontend_sorting?: number
  is_public?: boolean
  created_at?: string // TODO Decide whether to fetch it
  updated_at?: string

  related_attributes?: CalculatorTemplatePreset[]

  label_type: CalculatorTemplatePreset // e.g. 52
  quantity?: CalculatorTemplatePreset
  sorts?: CalculatorTemplatePreset
  width?: CalculatorTemplatePreset
  height?: CalculatorTemplatePreset
  dimension?: CalculatorTemplatePreset
  diameter?: CalculatorTemplatePreset
  label_shape?: CalculatorTemplatePreset // e.g. 54
  adhesive?: CalculatorTemplatePreset
  paper?: CalculatorTemplatePreset
  colour?: CalculatorTemplatePreset // 60 CMYK / 61 Black / 62 No color
  varnish?: CalculatorTemplatePreset
  opaque?: CalculatorTemplatePreset
  double_hit?: CalculatorTemplatePreset
  finishing?: CalculatorTemplatePreset
  foil?: CalculatorTemplatePreset
  die?: CalculatorTemplatePreset
  machine_processing?: CalculatorTemplatePreset
  winding_direction?: CalculatorTemplatePreset
  core_diameter?: CalculatorTemplatePreset
  assembly?: CalculatorTemplatePreset
  labels_on_roll?: CalculatorTemplatePreset
  max_roll_diameter?: CalculatorTemplatePreset
  artwork_service?: CalculatorTemplatePreset
  express_production?: CalculatorTemplatePreset
  super_express_production?: CalculatorTemplatePreset
  // Specially needed for standalone artwork services
  job_type?: ICalculatorTemplatePreset
}

export type CalculatorTemplatePreset = {
  id?: number
  template_id?: number
  attribute_name?: string
  value?: string | number | number[] | boolean // Stores a free value like quantity, sorts, width, height, etc.
  value_name?: string
  min?: number
  max?: number
  disabled?: boolean
  hidden?: boolean
  value_preset_list?: string[] | number[] | CalculatorTemplatePositiveList[]
  value_positive_list?: string[] | number[] | CalculatorTemplatePositiveList[]
}

export type CalculatorTemplatePositiveList = {
  id?: number
  template_attribute_id?: number
  value_id: number
}

export type Selection = {
  [key: string]: string | string[] | number | number[]
}

export type SettingsChildProps = {
  fields: IField[]
  texts: Step7
  step?: number
  wpText?: IgeneralPackagesQuery
  randomText?: RandomText
  initialSelection: Selection
  reset?: boolean
  setResetFalse?: () => void
  serviceType?: string
  isArtworkService?: boolean
  artworkServiceText: ArtworkServiceOptionModel
  helperText?: []
  dropdownDisabled?: boolean
  template?: IOrderTemplate
  compactView?: boolean
}

export type OptionsChildProps = {
  fields: IField[]
  texts: Step7
  wpText: IgeneralPackagesQuery
  isArtworkService?: boolean
  artworkServiceText: ArtworkServiceOptionModel
  initialSelection: Selection
  template?: IOrderTemplate
  compactView?: boolean
}

export enum Steps {
  SIZE_AND_FORMAT = 1,
  MATERIAL_AND_ADHESIVE = 2,
  COLOUR_AND_FINISHES = 3,
  REFINEMENT = 4,
  CUT = 5,
  MECHINE_PROCESSING = 6,
  OPTIONS = 7,
  SUMMARY = 8
}

export enum AustralianShopSteps {
  SIZE_AND_FORMAT = 1,
  MATERIAL_AND_ADHESIVE = 2,
  COLOUR_AND_FINISHES = 3,
  REFINEMENT = 4,
  CUT = 5,
  MECHINE_PROCESSING = 6,
  OPTIONS = 7,
  SUMMARY = 8
}

export enum AmericanShopSteps {
  SIZE_AND_FORMAT = 1,
  MATERIAL_AND_ADHESIVE = 2,
  COLOUR_AND_FINISHES = 3,
  REFINEMENT = 4,
  CUT = 5,
  MECHINE_PROCESSING = 6,
  OPTIONS = 7,
  SUMMARY = 8
}

export enum BasicProofSteps {
  SIZE_AND_FORMAT = 1,
  MATERIAL_AND_ADHESIVE = 2,
  COLOUR_AND_FINISHES = 3,
  OPTIONS = 4,
  SUMMARY = 5
}

export enum PremiumProofSteps {
  SIZE_AND_FORMAT = 1,
  MATERIAL_AND_ADHESIVE = 2,
  COLOUR_AND_FINISHES = 3,
  REFINEMENT = 4,
  MECHINE_PROCESSING = 5,
  OPTIONS = 6,
  SUMMARY = 7
}

export enum SmartServiceSteps {
  SIZE_AND_FORMAT = 1,
  MATERIAL_AND_ADHESIVE = 2,
  COLOUR_AND_FINISHES = 3,
  OPTIONS = 4,
  SUMMARY = 5
}

export enum ProServiceSteps {
  SIZE_AND_FORMAT = 1,
  MATERIAL_AND_ADHESIVE = 2,
  COLOUR_AND_FINISHES = 3,
  REFINEMENT = 4,
  OPTIONS = 5,
  SUMMARY = 6
}

export type StepStylesProps = {
  active: boolean
  finished?: boolean
}

export type SummaryChildProps = {
  calculatorFields: IField[]
  optionFields: IField[]
  setStep: (step: Steps) => void
  texts: Step8
  randomText?: RandomText
  isArtworkService: boolean
  production_time: IPrice
  template?: IOrderTemplate
  compactView?: boolean
  serviceType?: string
}

export type ResultProps = {
  result: IPrice
  texts: Resume
  error_messages?: ErrorMessages
  compactView?: boolean
}

export type ResultFooterProps = {
  texts: {
    button: Button
    extra: Extra
    help: Help
    more_info: {
      info_sentence: string
    }
    footer_message: string
  }
  compactView?: boolean
}

export type ArtworkServiceOptionProps = {
  artwork_service_model: ArtworkServiceOptionModel
}

/*
 *
 * Calculator fields based on each steps are handled here.
 * Fetch calculator fields from price API based on the below steps and fields.
 *
 */
export type ServiceResultProps = {
  proofing_service_model: ServiceModel
  type?: string
  price?: number
}

export const getCalculatorConstants = (serviceType: string | undefined, locale?: string): CalculatorStep[] => {
  switch (serviceType) {
    case 'basic':
      return [
        {
          step: BasicProofSteps.SIZE_AND_FORMAT,
          fields: [{ key: 'quantity' }, { key: 'dimension' }, { key: 'diameter' }, { key: 'label_shape' }]
        },
        {
          step: BasicProofSteps.MATERIAL_AND_ADHESIVE,
          fields: locale === 'us' ? [{ key: 'paper' }] : [{ key: 'adhesive' }, { key: 'paper' }]
        },
        {
          step: BasicProofSteps.COLOUR_AND_FINISHES,
          fields: [{ key: 'colour' }, { key: 'opaque' }, { key: 'double_hit' }]
        }
      ]
    case 'premium':
      return [
        {
          step: PremiumProofSteps.SIZE_AND_FORMAT,
          fields: [{ key: 'quantity' }, { key: 'dimension' }, { key: 'diameter' }, { key: 'label_shape' }]
        },
        {
          step: PremiumProofSteps.MATERIAL_AND_ADHESIVE,
          fields: locale === 'us' ? [{ key: 'paper' }] : [{ key: 'adhesive' }, { key: 'paper' }]
        },
        {
          step: PremiumProofSteps.COLOUR_AND_FINISHES,
          fields: [{ key: 'colour' }, { key: 'opaque' }, { key: 'double_hit' }, { key: 'varnish' }]
        },
        {
          step: PremiumProofSteps.REFINEMENT,
          fields: [{ key: 'finishing' }, { key: 'foil' }]
        },
        {
          step: PremiumProofSteps.MECHINE_PROCESSING,
          fields: [{ key: 'machine_processing' }, { key: 'winding_direction' }]
        }
      ]
    case 'smart':
      return [
        {
          step: SmartServiceSteps.SIZE_AND_FORMAT,
          fields: [{ key: 'quantity' }, { key: 'dimension' }, { key: 'diameter' }, { key: 'label_shape' }]
        },
        {
          step: SmartServiceSteps.MATERIAL_AND_ADHESIVE,
          fields: locale === 'us' ? [{ key: 'paper' }] : [{ key: 'adhesive' }, { key: 'paper' }]
        },
        {
          step: SmartServiceSteps.COLOUR_AND_FINISHES,
          fields: [{ key: 'colour' }]
        }
      ]
    case 'pro':
      return [
        {
          step: ProServiceSteps.SIZE_AND_FORMAT,
          fields: [{ key: 'quantity' }, { key: 'dimension' }, { key: 'diameter' }, { key: 'label_shape' }]
        },
        {
          step: ProServiceSteps.MATERIAL_AND_ADHESIVE,
          fields: locale === 'us' ? [{ key: 'paper' }] : [{ key: 'adhesive' }, { key: 'paper' }]
        },
        {
          step: ProServiceSteps.COLOUR_AND_FINISHES,
          fields: [{ key: 'colour' }, { key: 'opaque' }, { key: 'double_hit' }]
        },
        {
          step: ProServiceSteps.REFINEMENT,
          fields: [{ key: 'finishing' }, { key: 'foil' }]
        }
      ]
    case 'au':
      return [
        {
          step: AustralianShopSteps.SIZE_AND_FORMAT,
          fields: [{ key: 'quantity' }, { key: 'dimension' }, { key: 'diameter' }, { key: 'label_shape' }]
        },
        {
          step: AustralianShopSteps.MATERIAL_AND_ADHESIVE,
          fields: [{ key: 'adhesive' }, { key: 'paper' }]
        },
        {
          step: AustralianShopSteps.COLOUR_AND_FINISHES,
          fields: [{ key: 'colour' }, { key: 'opaque' }, { key: 'double_hit' }, { key: 'varnish' }]
        },
        {
          step: AustralianShopSteps.REFINEMENT,
          fields: [{ key: 'finishing' }, { key: 'foil' }]
        },
        {
          step: AustralianShopSteps.CUT,
          fields: [{ key: 'die' }]
        },
        {
          step: AustralianShopSteps.MECHINE_PROCESSING,
          fields: [
            { key: 'machine_processing' },
            { key: 'winding_direction' },
            { key: 'core_diameter' },
            { key: 'assembly' }
          ]
        }
      ]

    case 'us':
      return [
        {
          step: AmericanShopSteps.SIZE_AND_FORMAT,
          fields: [{ key: 'quantity' }, { key: 'dimension' }, { key: 'diameter' }, { key: 'label_shape' }]
        },
        {
          step: AmericanShopSteps.MATERIAL_AND_ADHESIVE,
          fields: [{ key: 'paper' }]
        },
        {
          step: AmericanShopSteps.COLOUR_AND_FINISHES,
          fields: [{ key: 'colour' }, { key: 'opaque' }, { key: 'double_hit' }, { key: 'varnish' }]
        },
        {
          step: AmericanShopSteps.REFINEMENT,
          fields: [{ key: 'finishing' }, { key: 'foil' }]
        },
        {
          step: AmericanShopSteps.CUT,
          fields: [{ key: 'die' }]
        },
        {
          step: AmericanShopSteps.MECHINE_PROCESSING,
          fields: [
            { key: 'machine_processing' },
            { key: 'winding_direction' },
            { key: 'core_diameter' },
            { key: 'assembly' }
          ]
        }
      ]

    // Only for reorder calculator
    // My account -> Label library -> Reoder functionality
    case 'reorder':
      return [
        {
          step: ProServiceSteps.SIZE_AND_FORMAT,
          fields: [{ key: 'dimension' }, { key: 'quantity' }]
        }
      ]
    default:
      return [
        {
          step: Steps.SIZE_AND_FORMAT,
          fields: [
            { key: 'label_type' },
            { key: 'quantity' },
            { key: 'dimension' },
            { key: 'diameter' },
            { key: 'label_shape' }
          ]
        },
        {
          step: Steps.MATERIAL_AND_ADHESIVE,
          fields: [{ key: 'adhesive' }, { key: 'paper' }]
        },
        {
          step: Steps.COLOUR_AND_FINISHES,
          fields: [{ key: 'colour' }, { key: 'opaque' }, { key: 'double_hit' }, { key: 'varnish' }]
        },
        {
          step: Steps.REFINEMENT,
          fields: [{ key: 'finishing' }, { key: 'foil' }]
        },
        {
          step: Steps.CUT,
          fields: [{ key: 'die' }]
        },
        {
          step: Steps.MECHINE_PROCESSING,
          fields: [
            { key: 'machine_processing' },
            { key: 'winding_direction' },
            { key: 'core_diameter' },
            { key: 'assembly' }
          ]
        }
      ]
  }
}

export enum ARTWORKSERVICE_SELECTION {
  NO_ARTWORK_SERVICE = 100,
  SMART_ARTWORK_SERVICE = 101,
  PRO_ARTWORK_SERVICE = 102
}

export enum PROOF_TYPE {
  BASIC_PROOF = 2,
  PREMIUM_PROOF = 4
}

export const schema = (texts: ErrorMessages | undefined) =>
  z.object({
    quotation_cust_name: z.string().min(1, texts?.required),
    quotation_cust_mail: z.string().email(texts?.invalid_email)
  })
